export type FairgenConfidenceTier = 'strong' | 'solid' | 'cautious' | 'do_not_call'

export const FAIRGEN_TIER_LABELS: Record<FairgenConfidenceTier, string> = {
  strong: 'Strong',
  solid: 'Solid',
  cautious: 'Cautious',
  do_not_call: 'Do not call',
}

/** E1 §7 — fixed sentences. Never LLM-authored. */
export const FAIRGEN_TIER_SENTENCES: Record<FairgenConfidenceTier, string> = {
  strong: 'Fairgen confidence is strong: the tests agree and the lead is clear.',
  solid: 'Fairgen confidence is solid: the lead holds with only minor caveats.',
  cautious: 'Fairgen confidence is cautious, so treat this as directional.',
  do_not_call: 'Fairgen confidence is too low to recommend a winner.',
}

const FAIRGEN_TIERS: Array<FairgenConfidenceTier> = [
  'strong',
  'solid',
  'cautious',
  'do_not_call',
]

export interface StudyDocFairgenConfidence {
  tier: FairgenConfidenceTier
  sentence?: string
}

export interface StudyDocDecisionVersionOdds {
  id: string
  pBestPct: number
  /** Display label when the wire already resolved one. Absent → show `id`. */
  label?: string
}

/**
 * E1 output model (FE camelCase) plus optional Fairgen / recommendation.
 * The masthead also accepts the snake_case wire fields via the input shape.
 */
export interface StudyDocDecisionConfidence {
  leaderId: string
  pBestPct: number
  n: number
  basis?: 'paired_bootstrap' | 'two_proportion'
  claim?: string
  perVersion?: Array<StudyDocDecisionVersionOdds>
  /** id → display label. Used when a per-version row has no `label`. */
  labels?: Record<string, string>
  recommendation?: string
  fairgenConfidence?: StudyDocFairgenConfidence
}

export interface StudyDocDecisionVersionOddsInput {
  id?: string
  pBestPct?: number
  p_best_pct?: number
  label?: string
}

export interface StudyDocDecisionConfidenceInput {
  leaderId?: string
  leader_id?: string
  pBestPct?: number
  p_best_pct?: number
  n?: number
  basis?: string
  claim?: string
  perVersion?: Array<StudyDocDecisionVersionOddsInput>
  per_version?: Array<StudyDocDecisionVersionOddsInput>
  labels?: Record<string, string>
  recommendation?: string
  fairgenConfidence?: StudyDocFairgenConfidence | Record<string, unknown>
  fairgen_confidence?: StudyDocFairgenConfidence | Record<string, unknown>
}

export function isFairgenConfidenceTier(value: string): value is FairgenConfidenceTier {
  return FAIRGEN_TIERS.includes(value as FairgenConfidenceTier)
}

function asFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  return undefined
}

function asNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function normalizeTierToken(value: string): string {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

export function normalizeFairgenConfidence(raw: unknown): StudyDocFairgenConfidence | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const row = raw as Record<string, unknown>
  const tierRaw = asNonEmptyString(row.tier)
  if (!tierRaw) {
    return undefined
  }
  const tier = normalizeTierToken(tierRaw)
  if (!isFairgenConfidenceTier(tier)) {
    return undefined
  }
  const sentence = asNonEmptyString(row.sentence)
  const next: StudyDocFairgenConfidence = { tier }
  if (sentence) {
    next.sentence = sentence
  }
  return next
}

function normalizePerVersion(raw: unknown): Array<StudyDocDecisionVersionOdds> | undefined {
  if (!Array.isArray(raw)) {
    return undefined
  }
  const items = raw.flatMap((entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return []
    }
    const item = entry as Record<string, unknown>
    const id = asNonEmptyString(item.id)
    const pct = asFiniteNumber(item.pBestPct ?? item.p_best_pct)
    if (!id || pct === undefined) {
      return []
    }
    const label = asNonEmptyString(item.label)
    const row: StudyDocDecisionVersionOdds = { id, pBestPct: pct }
    if (label) {
      row.label = label
    }
    return [row]
  })
  return items.length > 0 ? items : undefined
}

export function normalizeStudyDocDecisionConfidence(
  raw: unknown,
): StudyDocDecisionConfidence | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const row = raw as StudyDocDecisionConfidenceInput & Record<string, unknown>
  const leaderId = asNonEmptyString(row.leaderId ?? row.leader_id)
  const pBestPct = asFiniteNumber(row.pBestPct ?? row.p_best_pct)
  const n = asFiniteNumber(row.n)
  if (!leaderId || pBestPct === undefined || n === undefined) {
    return undefined
  }

  const next: StudyDocDecisionConfidence = {
    leaderId,
    pBestPct,
    n,
  }

  if (row.basis === 'paired_bootstrap' || row.basis === 'two_proportion') {
    next.basis = row.basis
  }

  const claim = asNonEmptyString(row.claim)
  if (claim) {
    next.claim = claim
  }

  const perVersion = normalizePerVersion(row.perVersion ?? row.per_version)
  if (perVersion) {
    next.perVersion = perVersion
  }

  const labels = normalizeVersionLabels(row.labels)
  if (labels) {
    next.labels = labels
  }

  const recommendation = asNonEmptyString(row.recommendation)
  if (recommendation) {
    next.recommendation = recommendation
  }

  const fairgen = normalizeFairgenConfidence(row.fairgenConfidence ?? row.fairgen_confidence)
  if (fairgen) {
    next.fairgenConfidence = fairgen
  }

  return next
}

export function isStudyDocDecisionMastheadVisible(
  decisionConfidence: unknown,
  isHidden?: boolean,
): boolean {
  if (isHidden) {
    return false
  }
  return Boolean(normalizeStudyDocDecisionConfidence(decisionConfidence))
}

function normalizeVersionLabels(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return undefined
  }
  const next: Record<string, string> = {}
  Object.entries(raw as Record<string, unknown>).forEach(([key, value]) => {
    const id = asNonEmptyString(key)
    const label = asNonEmptyString(value)
    if (id && label) {
      next[id] = label
    }
  })
  return Object.keys(next).length > 0 ? next : undefined
}

function versionDisplayName(
  entry: StudyDocDecisionVersionOdds,
  confidence: StudyDocDecisionConfidence,
): string {
  return (
    asNonEmptyString(entry.label) ??
    asNonEmptyString(confidence.labels?.[entry.id]) ??
    entry.id
  )
}

/** E1 §3 claim templates — derived at render when the wire claim is absent. */
export function formatPBestHeroLine(leaderId: string, pBestPct: number, n: number): string {
  const p = Math.round(pBestPct)
  if (p >= 85) {
    return `${leaderId} wins in ${p} of 100 resamples (n=${n}).`
  }
  if (p >= 70) {
    return `${leaderId} leads in ${p} of 100 resamples (n=${n}). Directional.`
  }
  return `Too close to call: ${leaderId} at ${p} of 100 resamples is not separated (n=${n}).`
}

export function formatPBestBullets(confidence: StudyDocDecisionConfidence): Array<string> {
  const authoredClaim = asNonEmptyString(confidence.claim)
  const hero =
    authoredClaim ?? formatPBestHeroLine(confidence.leaderId, confidence.pBestPct, confidence.n)
  const extras = (confidence.perVersion ?? [])
    .filter((entry) => entry.id !== confidence.leaderId)
    .slice(0, 2)
    .map(
      (entry) =>
        `${versionDisplayName(entry, confidence)}: ${Math.round(entry.pBestPct)} of 100 resamples`,
    )
  return [hero, ...extras].slice(0, 3)
}

export function formatFairgenTierLabel(tier: FairgenConfidenceTier): string {
  return FAIRGEN_TIER_LABELS[tier]
}

export function formatFairgenSentence(fairgen: StudyDocFairgenConfidence): string {
  const authored = fairgen.sentence?.trim()
  if (authored) {
    return authored
  }
  return FAIRGEN_TIER_SENTENCES[fairgen.tier]
}
